import { KeyboardEvent, MouseEvent as ReactMouseEvent } from "react";
import { connect, MapDispatchToProps, MapStateToProps, MergeProps } from "react-redux";

import { doNothing, Key, toSerializableKeyEvent } from "@multiweb/reactUtils";
import { IAppState, QAProps } from "@multiweb/types/react";
import { createMenuItemActivatedAction } from "../../actions";
import { isItemFocused, MenuFocusableItemType, NavigationSection } from "../../navigation";
import {
    areMenuPanelCaptionsVisible,
    getPanelItemActivationType,
    getPanelItemCaption,
    getPanelItemIconCode,
    getPanelItemItemId,
    getPanelItemTitle,
    isMenuVisible,
    isPanelItemActive,
    isPanelItemCombinedType,
    isPanelItemHighlighted,
} from "../../selectors";
import { MenuItemActivatedType } from "../../types";
import { createMenuItemMouseActivationHandler, createOpenableInTabItemMouseActivationHandler, isMenuItemActivatedByKey } from "../../utils";
import { isRightPartEvent, MenuPanelItemComponent, MenuPanelItemComponentProps } from "../MenuPanelItemComponent";
import { isExpandArrowVisible } from "./utils";

export type MenuPanelItemProps = {
    /**
     * Identifikátor položky panelu menu. Statické identifikátory jsou dostupné ve výčtu
     * @see MenuPanelItemId
     */
    readonly id: string;
    /** Sekce, ve které je položka vykreslená. Slouží pro určování zaměření. */
    readonly section: NavigationSection;
} & QAProps;

type StateProps = {
    isMenuVisible: ReturnType<typeof isMenuVisible>;
    itemId: string | number;
    activationType: MenuItemActivatedType | undefined;
    isCombinedItem: boolean;
} & Pick<MenuPanelItemComponentProps, "iconCode" | "isFocused" | "isHighlighted" | "title" | "caption" | "higher">
  & Required<Pick<MenuPanelItemComponentProps, "isActive">>;

const mapStateToProps: MapStateToProps<StateProps, MenuPanelItemProps, IAppState> = (state, { id, section }) => ({
    iconCode: getPanelItemIconCode(state, id),
    isActive: isPanelItemActive(state, id),
    isHighlighted: isPanelItemHighlighted(state, id),
    title: getPanelItemTitle(state, id),
    isFocused: isItemFocused(state, { itemId: id, type: MenuFocusableItemType.panelItem, section }),
    isMenuVisible: isMenuVisible(state),
    caption: getPanelItemCaption(state, id),
    higher: areMenuPanelCaptionsVisible(state),
    itemId: getPanelItemItemId(state, id),
    activationType: getPanelItemActivationType(state, id),
    isCombinedItem: isPanelItemCombinedType(state, id),
});

type DispatchProps = {
    panelItemActivated: typeof createMenuItemActivatedAction;
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps, MenuPanelItemProps> = {
    panelItemActivated: createMenuItemActivatedAction,
};

const mergeProps: MergeProps<StateProps, DispatchProps, MenuPanelItemProps, MenuPanelItemComponentProps> = (
    { isActive, isMenuVisible, itemId, activationType, isCombinedItem, ...restStateProps },
    { panelItemActivated },
    { qa, section, id },
) => {
    const expandArrowVisible = isExpandArrowVisible(isCombinedItem, activationType, id);
    return ({
        isActive,
        isExpandArrowVisible: expandArrowVisible,
        showSeparator: isCombinedItem,
        ...restStateProps,
        onClick: activationType ? createPanelItemMouseUpHandler(itemId, activationType, section, panelItemActivated, expandArrowVisible) : doNothing,
        onKeyUp: activationType ? createPanelItemActionKeyHandler(itemId, activationType, section, panelItemActivated, isActive, isMenuVisible, expandArrowVisible) : doNothing,
        qa,
        section,
        id,
    });
};

/**
 * Napojuje komponenty tlačítek levého panelu menu. Na základě id sekce pro ně získá ikonu, popisek a určí zda je dané tlačítko aktivní.
 * Definuje onClick akci, která vybírá aktivní sekci menu.
 */
export const MenuPanelItem = connect(mapStateToProps, mapDispatchToProps, mergeProps)(MenuPanelItemComponent);

/**
 * Vytvoří funkci pro zpracování událostí stisknutí kláves, která zavolá zadanou funkci, pokud byla stisknuta klávesa enter nebo šipka vpravo.
 * Určeno pouze pro položky levého panelu menu. Pokud je aktivace provedena pravou šipkou, rozhoduje se o jejím zpracování na základě aktivní položky a toho,
 * zda je otevřené menu. Pokud je položka aktivní a menu je otevřené, propaguje se událost dál a zpracuje se jako klasická navigační klávesa. V případě,
 * že je třeba aktivovat sekci, nebo otevřít menu, se chová jako aktivační klávesa. V tomto případě provede přesun zaměření sága obsluhující aktivaci sekce (kvůli
 * konzistenci mezi aktivací myší a aktivací klávesnicí). S propagací by se provedl dvojitý posun, druhý ze ságy zpracovávající události kláves na úrovni celého menu.
 * Položky formulářů v menu panelu nikdy nereagují na pravou šipku.
 *
 * V případě kombinovaného tlačítka se mohou vyslat i dvě akce najednou - pro levou i pravou část. Které akce se vyšlou záleží na způsobu aktivace a viditelnosti pravé části tlačítka.
 * Pro aktivaci pravou šipkou platí podmínky zmíněné výše, nezávisle na tom, že se jedná o kombinované tlačítko.
 *
 *|          Tabulka aktivace            |   Pravá část viditelná   |   Pravá část skrytá                       |
 *|--------------------------------------|--------------------------|-------------------------------------------|
 *| Aktivace šipkou vpravo               |    Aktivace složky       |   Aktivace složky                         |
 *| Aktivace akční klávesou (ENTER/SPACE)|    Aktivace formuláře    |   Aktivace formuláře + Aktivace složky    |
 *
 * @param itemId Identifikátor položky unikátní v rámci jejího typu.
 * @param itemType Typ položky.
 * @param section Sekce, ve které je položka vykreslená.
 * @param onActivated Funkce, která se má zavolat při stisknutí klávesy enter, nebo šipky vpravo, pokud má fungovat jako enter.
 * @param isActive Příznak, zda je položka menu panelu aktivní.
 * @param isMenuVisible Příznak, zda je menu viditelné.
 * @param isExpandArrowVisible Příznak, zda je v položce menu zobrazená rozbalovací šipka.
 */
export const createPanelItemActionKeyHandler = (
    itemId: string | number,
    itemType: MenuItemActivatedType,
    section: NavigationSection,
    onActivated: typeof createMenuItemActivatedAction,
    isActive: boolean,
    isMenuVisible: boolean,
    isExpandArrowVisible: boolean,
) => (event: KeyboardEvent) => {
    const activatedByRightArrowKey = event.key === Key.ArrowRight && (!isActive || !isMenuVisible) && itemType !== MenuItemActivatedType.panelItemForm;
    if (activatedByRightArrowKey) {
        event.stopPropagation();
    }
    const activatedByActionKey = isMenuItemActivatedByKey(event);
    if (activatedByActionKey || activatedByRightArrowKey) {
        const serializedKeyEvent = toSerializableKeyEvent(event);
        if (itemType === MenuItemActivatedType.panelItemCombinedItemForm) { // kombinované tlačítko
            if (activatedByActionKey) { // formulář se aktivuje jen akční klávesou, nikdy šipkou vpravo
                onActivated(itemId, MenuItemActivatedType.panelItemCombinedItemForm, section, undefined, serializedKeyEvent);
            }
            if (activatedByRightArrowKey || !isExpandArrowVisible) { // složka se aktivuje pravou šipkou, nebo i akční klávesou, pokud není viditelná pravá část tlačítka
                onActivated(itemId, MenuItemActivatedType.panelItemCombinedItemFolder, section, undefined, serializedKeyEvent);
            }
        } else { // klasický panel item
            onActivated(itemId, itemType, section, undefined, serializedKeyEvent);
        }
    }
};

/**
 * Vytvoří funkci pro zpracování události kliknutí myši. Rozlišuje zpracování události pro případ kombinovaného tlačítka, kdy vybere typ aktivované položky na základě původu události
 * a stavu viditelnosti pravé části tlačítka. Kliknutí na pravou část aktivuje část složky. Kliknutí na levou část záleží na viditelnosti pravé část. Pokud je viditelná, aktivuje část formuláře.
 * Pokud viditelná není, aktivuje část formuláře i část složky.
 * @param itemId Identifikátor položky unikátní v rámci jejího typu.
 * @param itemType Typ položky.
 * @param section Sekce, ve které je položka vykreslená.
 * @param onActivated Funkce, která vysílá akci aktivace položky.
 * @param isExpandArrowVisible Příznak, zda je v položce menu zobrazená rozbalovací šipka.
 */
export const createPanelItemMouseUpHandler = (
    itemId: string | number,
    itemType: MenuItemActivatedType,
    section: NavigationSection,
    onActivated: typeof createMenuItemActivatedAction,
    isExpandArrowVisible: boolean,
) => {
    if (itemType === MenuItemActivatedType.panelItemForm) {
        return createOpenableInTabItemMouseActivationHandler(itemId, itemType, section, onActivated);
    } else if (itemType === MenuItemActivatedType.panelItemCombinedItemForm) {
        return (event: ReactMouseEvent) => {
            if (isRightPartEvent(event)) {// kliknutí na pravou část otevírá složku
                createMenuItemMouseActivationHandler(itemId, MenuItemActivatedType.panelItemCombinedItemFolder, section, onActivated)(event);
            } else {
                createOpenableInTabItemMouseActivationHandler(itemId, MenuItemActivatedType.panelItemCombinedItemForm, section, onActivated)(event);
                if (!isExpandArrowVisible) { // pokud není pravá část viditelná, kliknutí na levou část aktivuje i pravou část - složku
                    createMenuItemMouseActivationHandler(itemId, MenuItemActivatedType.panelItemCombinedItemFolder, section, onActivated)(event);
                }
            }
        };
    } else {
        return createMenuItemMouseActivationHandler(itemId, itemType, section, onActivated);
    }
};
